import _ from 'underscore';

import { PredicateList } from '@bingads-webui/predicate-list-react';
import { getEntityFromScope } from '@bingads-webui/edm-campaign';
import { campaignBulkEdit } from '@bingads-webui-campaign/campaign-bulk-edit';
import { showPausedCampaignNotifications, showBudgetBidCampaignNotifications } from '@bingads-webui-campaign/import-shared-utils';
import { placeholderPlugin } from '@bingads-webui/projection-grid-placeholder';
import { automatedRulesPlugin } from '@bingads-webui-campaign/projection-grid-automated-rules';
import { labelsPlugin } from '@bingads-webui-campaign/projection-grid-labels';
import { triggerQualtricsEvent } from '@bingads-webui-campaign/qualtrics';
import { pastePlugin } from '@bingads-webui-campaign/projection-grid-paste';
import { projectionGridScopeNavigator } from '@bingads-webui-campaign/projection-grid-scope-navigator';
import { projectionGridPopupPlugin } from '@bingads-webui-campaign/projection-grid-popup';
import { qualityScoreTitleTemplate, qualityScoreContentTemplate } from '@bingads-webui-campaign/quality-score';
import { CampaignAdgroupSelectorEdmView } from '@bingads-webui-campaign-react/campaign-adgroup-selector-edm';
import { selectionStatePlugin } from '@bingads-webui-campaign/pgrid-selection-state-plugin';
import { trackingPlugin } from '@bingads-webui-campaign/projection-grid-tracking';
import { getPGridColumnResizePlugin } from '@bingads-webui-campaign/projection-grid-column-resize-helper';
import { projectionGridFlexColumnResize } from '@bingads-webui-campaign/projection-grid-flex-column-resize';
import { projectionGridScrollbarPlugin } from '@bingads-webui-campaign/projection-grid-scrollbar';
import ChangeBudgetView from '@bingads-webui-static-legacy/bulk-edit/view/campaign/changeBudget';
import BulkCampaignExclusionsView from '@bingads-webui-campaign-legacy/bulk-campaign-exclusions/view/bulk-campaign-exclusions';
import ChangeBudgetModel from '@bingads-webui-static-legacy/bulk-edit/model/campaign/changeBudget';
import AudienceAssociationView from '@bingads-webui-campaign-legacy/audience-bulk-edit-campaign-association/view/association';
import AudienceAssociationModel from '@bingads-webui-campaign-legacy/audience-bulk-edit-campaign-association/model/association';
import ChangeCashbackBudgetView from '@bingads-webui-static-legacy/bulk-edit/view/campaign/changeCashbackBudget';
import ChangeCashbackBudgetModel from '@bingads-webui-static-legacy/bulk-edit/model/campaign/changeCashbackBudget';
import ChangeCashbackPercentView from '@bingads-webui-static-legacy/bulk-edit/view/campaign/changeCashbackPercent';
import ChangeCashbackPercentModel from '@bingads-webui-static-legacy/bulk-edit/model/campaign/changeCashbackPercent';
import ChangeRsaAutoAssetsView from '@bingads-webui-static-legacy/bulk-edit/view/shared/changeRsaAutoAssets';
import ChangeRsaAutoAssetsModel from '@bingads-webui-static-legacy/bulk-edit/model/shared/changeRsaAutoAssets';

const { noop } = _;

export const getAdditionalPlugins = ({
  gridName,
  toolbarPlugin,
  filterService,
  scope,
  appConfig,
  ChangeStatusView,
  ChangeStatusModel,
  ChangeBidStrategyModel,
  ChangeBidStrategyView,
  ChangeLanguageView,
  ChangeLanguageModel,
  CopyPaste,
  crossPageSelectPlugin,
  i18n,
  newI18n,
  i18nGlobal,
  instrumentation,
  odata,
  appState,
  stateManager,
  preferenceService,
  i18nErrors,
  jsData,
  LabelAssociationView,
  dataContextManager,
  cacheService,
  s$label,
  router,
  permissions,
  edm,
  getCampaignTypeGlobalFilter,
  perfMarker,
  campaignUpgradeProps,
  cacheManager,
}) => {
  const campaignStatus = {
    Active: 'Active',
    Deleted: 'Deleted',
    Paused: 'UserPaused',
    Suspended: 'Suspended',

    // used in wunderbar, needs to be migrated once workflow goes to oData
    EnumPaused: 124,
    EnumBudgetPaused: 126,
    EnumBudgetUserPaused: 127,
  };

  const enableSelectionState = permissions.dynamic.SelectionStatePluginOnCampaign;

  const { activity: pausedCampaignImportNotificationActivity } = instrumentation.createScenarioAndActivity({
    scenarioName: 'PausedCampaignImportNotification',
    activityName: 'CampaignsGridBulkEdit',
  });


  const { activity: BudgetBidCampaignImportNotificationActivity } = instrumentation.createScenarioAndActivity({
    scenarioName: 'BudgetBidCampaignImportNotification',
    activityName: 'CampaignsGridBulkEdit',
  });

  const bulkEditPlugin = campaignBulkEdit({
    status: campaignStatus,
    ChangeStatusView,
    ChangeStatusModel,
    filterService,
    scope,
    ChangeBudgetView,
    ChangeBudgetModel,
    ChangeCashbackBudgetView,
    ChangeCashbackBudgetModel,
    ChangeCashbackPercentView,
    ChangeCashbackPercentModel,
    ChangeBidStrategyModel,
    ChangeBidStrategyView,
    ChangeLanguageView,
    ChangeLanguageModel,
    BulkCampaignExclusionsView,
    AudienceAssociationView,
    AudienceAssociationModel,
    ChangeRsaAutoAssetsView,
    ChangeRsaAutoAssetsModel,
    campaignUpgradeProps,
    CopyPaste,
    config: appConfig,
    i18n,
    i18nNewDesign: newI18n,
    router,
    isForVNext: true,
    qualtricsSurveyCallback: () => triggerQualtricsEvent('UserPausedCampaignMainLineGrid'),
    importNotificationCallback: ({ campaignItemList, updateStatus }) => showPausedCampaignNotifications({ campaignItemList, updateStatus, activity: pausedCampaignImportNotificationActivity }), // show paused campaign notifications based on specific conditions
    importBudgetBidNotificationCallback: ({ campaignItemList, newBudget, newBidStrategy }) => showBudgetBidCampaignNotifications({
      campaignItemList,
      newBudget,
      newBidStrategy,
      activity: BudgetBidCampaignImportNotificationActivity,
    }), // show budget and bid campaign notifications based on specific conditions
    disableDeselectAll: enableSelectionState,
    getCampaignTypeGlobalFilter,
    perfMarker,
    cacheManager,
  });

  return () => _.compact([
    {
      plugin: crossPageSelectPlugin.plugin,
      config: _.extend({}, crossPageSelectPlugin.config, {
        disableDeselectAll: enableSelectionState,
      }),
    },
    toolbarPlugin,
    {
      plugin: definePlugin => definePlugin('filterState', [], noop),
    },
    {
      plugin: bulkEditPlugin,
    },
    {
      plugin: automatedRulesPlugin,
      config: {
        isForVNext: true,
        entityName: 'Campaign',
        i18n,
        i18nGlobal,
        router,
        instrumentation,
        currentUserId: appConfig.get('CurrentUser').Id,
        permissions,
        scope,
        accountTimeZone: appConfig.get('AccountTimeZone'),
        accountCurrencyCode: appConfig.get('AccountCurrencyCode'),
        state: appState,
        odata,
        stateMgr: stateManager,
        preferenceSvc: preferenceService,
        appCfg: appConfig,
        bidRange: appConfig.get('KeywordBidRange'),
        budgetRange: appConfig.get('BudgetRange').DailyRange,
        newI18n,
        CampaignAdgroupSelectorEdmView,
        currentEntity: getEntityFromScope(scope).currentAccount,
        PredicateListReact: PredicateList,
      },
    },
    {
      plugin: labelsPlugin,
      config: {
        isForVNext: true,
        i18n,
        i18nErrors,
        router,
        jsdata: jsData,
        LabelAssociationView,
        dataContextManager,
        cacheService,
        instrumentation,
        s$label,
        disableDeselectAll: true,
        scope,
        edm,
        perfMarker,
      },
    },
    {
      plugin: projectionGridScopeNavigator,
      config: scope,
    },
    {
      plugin: projectionGridPopupPlugin,
      config: [{
        selector: '.quality-score-tooltip',
        outerSelector: '.grid-column-QualityScore',
        container: 'body',
        className: 'quality-score-popup',
        content: value => qualityScoreContentTemplate({ value, i18n }),
        title: value => qualityScoreTitleTemplate({ value, i18n }),
      }],
    },
    {
      plugin: definePlugin => definePlugin('placeholder', [], noop), // nothing actually depends on this, just a reminder of plugins which have been removed
    },
    {
      plugin: placeholderPlugin,
      config: {
        footerVisableElement: ['.scrollbar'],
        // Message for no results when using filter
        filteredContainer: '.campaigns-table .grid-noresult',
        filteredEmptyText: i18n.getString('Grid_NoFilteredRecords'),

        // Message for no data at all.
        container: '.campaigns-table .grid-noresult',
        text: newI18n.getString(_TL_('Looks like you haven\'t created any campaigns yet. Click the create campaign button to get started.')),
      },
    },
    {
      plugin: pastePlugin,
      config: {
        i18n,
        CopyPaste,
        filterService,
        perfMarker,
      },
    },
    {
      plugin: trackingPlugin,
      config: {
        entityType: 'Campaign',
        trackers: {
          ViewTracker: {
            timeWindow: 500,
            enabled: true,
          },
        },
        enabled: permissions.IsTrackingPluginEnabled,
      },
    },
    enableSelectionState && {
      plugin: selectionStatePlugin,
    },
    ...permissions.dynamic.GridFlexLayout ? [{
      plugin: projectionGridScrollbarPlugin,
      config: {
        scrollElementSelector: '.grid-group-other',
      },
    }, {
      plugin: projectionGridFlexColumnResize,
      config: {
        gridName,
        softDependencies: ['popColumns'],
        preferenceService,
      },
    }] : [getPGridColumnResizePlugin({
      gridName,
      preferenceService,
      permissions,
    })],
  ]);
};
